import { Link } from "react-router-dom";
import { Wallet, Target, ListChecks, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStore } from "@/store/AppStore";
import { PageHeader, Panel, KpiCard, ProgressLine } from "@/components/kit";
import { BarsChart, DonutChart, GOLD, GREY } from "@/components/Charts";
import { RankingTable } from "@/components/Lists";
import { companySummary, companyHistory, ranking, teamOf, rate, pendingProofs, nameMap } from "@/lib/selectors";
import { isOverdue, taskStats, sortTasks, assigneeLabel } from "@/lib/tasks";
import { Tag } from "@/components/kit";
import { PresenceSummary } from "@/components/Attendance";
import { BirthdayPanel } from "@/components/Birthdays";
import { fmtDate } from "@/lib/format";
import { PROFILES, MONTH_LABEL } from "@/data/mock";
import { brl } from "@/lib/format";

export default function OwnerDashboard() {
  const { state } = useStore();
  const cid = PROFILES.empresaria.companyId;
  const company = state.companies.find((c) => c.id === cid);
  const r = companySummary(state, company);
  const rank = ranking(state, cid);
  const team = teamOf(state, cid);
  const checklists = state.checklists.filter((c) => c.companyId === cid);

  return (
    <div data-testid="owner-dashboard-page">
      <PageHeader eyebrow={MONTH_LABEL} title={`Olá, ${PROFILES.empresaria.name.split(" ")[0]}`} subtitle={`Resumo da ${company.name} hoje.`} actions={<Button asChild className="font-semibold" data-testid="owner-launch-sale-button"><Link to="/empresaria/vendas">Lançar venda</Link></Button>} />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testId="owner-kpi-sales" icon={Wallet} label="Vendas do mês" value={brl(r.sales)} hint={`Meta ${brl(company.salesGoal)}`} />
        <KpiCard testId="owner-kpi-goal" icon={Target} label="Meta atingida" value={`${r.att}%`} hint={`Faltam ${brl(Math.max(company.salesGoal - r.sales, 0))}`} />
        <KpiCard testId="owner-kpi-tasks" icon={ListChecks} label="Tarefas concluídas" value={`${r.taskRate}%`} hint="Concluídas pela equipe" />
        <KpiCard testId="owner-kpi-overdue" icon={AlertTriangle} label="Atrasadas" value={r.overdue} hint="Tarefas fora do prazo" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="Vendas mensais" subtitle="Últimos 6 meses x meta" className="lg:col-span-2" testId="owner-chart-monthly">
          <BarsChart data={companyHistory(state, company)} xKey="mes" series={[{ key: "vendas", name: "Vendas", color: GOLD }, { key: "meta", name: "Meta", color: GREY }]} />
        </Panel>
        <Panel title="Vendas por colaboradora" subtitle="Mês atual" testId="owner-chart-team">
          <DonutChart data={rank.map((x) => ({ name: x.name.split(" ")[0], value: x.sales }))} height={170} />
        </Panel>
        <Panel title="Ranking do mês" className="lg:col-span-2" testId="owner-ranking-preview" action={<Link to="/empresaria/ranking" className="text-xs font-semibold text-gold-dark hover:underline" data-testid="owner-see-ranking">Ver ranking</Link>}>
          <RankingTable rows={rank.slice(0, 3)} />
        </Panel>
        <Panel title="Checklists de hoje" subtitle="Conclusão por colaboradora" testId="owner-checklists-status" action={<Link to="/empresaria/processos" data-testid="owner-pending-proofs-link" className="rounded-full bg-gold-soft px-2.5 py-0.5 text-[11px] font-semibold text-gold-dark hover:underline">{pendingProofs(state, cid).length} provas pendentes</Link>}>
          <ul className="space-y-5">
            {team.map((c) => {
              const items = checklists.filter((cl) => cl.collaboratorId === c.id).flatMap((cl) => cl.items);
              return (
                <li key={c.id}>
                  <div className="mb-2 flex justify-between text-sm"><span className="font-medium">{c.name.split(" ")[0]}</span><span className="text-xs font-semibold text-neutral-500">{rate(items)}%</span></div>
                  <ProgressLine value={rate(items)} />
                </li>
              );
            })}
          </ul>
        </Panel>
        <PresenceSummary companyId={cid} />
        <BirthdayPanel companyIds={[cid]} className="lg:col-span-3" />
        <PendingTasks state={state} cid={cid} />
      </div>
    </div>
  );
}

function PendingTasks({ state, cid }) {
  const tasks = sortTasks(state.tasks.filter((t) => t.companyId === cid && (isOverdue(t) || t.status === "aguardando")));
  const st = taskStats(state.tasks.filter((t) => t.companyId === cid));
  const names = nameMap(state);
  return (
    <Panel title="Atrasadas e pendências" subtitle={`${st.overdue} atrasada(s) · ${st.awaiting} aguardando aprovação · ${st.open} em aberto`} className="lg:col-span-3" testId="owner-pending-tasks" action={<Link to="/empresaria/equipe" className="text-xs font-semibold text-gold-dark hover:underline" data-testid="owner-see-tasks">Ver tarefas</Link>}>
      {!tasks.length && <p className="text-sm text-neutral-400">Nenhuma pendência. Equipe em dia!</p>}
      <ul className="divide-y divide-neutral-100">
        {tasks.slice(0, 6).map((t) => (
          <li key={t.id} data-testid={`pending-task-${t.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3 text-sm">
            <span className="min-w-0 flex-1 font-medium">{t.title}</span>
            <span className="text-xs text-neutral-500">{assigneeLabel(t, names)} · prazo {fmtDate(t.dueDate)}</span>
            <Tag tone={isOverdue(t) ? "red" : "dark"}>{isOverdue(t) ? "Atrasada" : "Aguardando aprovação"}</Tag>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
